import React, { useMemo, useState } from 'react';
import { UsersIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
//import { ClockIcon, CalendarDaysIcon } from '@heroicons/react/20/solid';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/solid';
import { useBooking } from '../../context/BookingContext';
import { Link, useNavigate } from 'react-router-dom';
//import { Switch } from '@headlessui/react';
import { addDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import AutoCompleteInput from '../../components/AutocompleteInput';

const CounterItem = ({ label, icon: Icon, value, onChange, min = 0, max = 1 }) => {
  const handleDecrease = () => {
    if (value > min) onChange(value - 1);
  };

  const handleIncrease = () => {
    if (value < max) onChange(value + 1);
  };

  return (
    <div className="flex h-[60px] items-center justify-between rounded-lg border border-gray-300 bg-white p-3 shadow-sm">
      {/* Lato Sinistro: Icona e Etichetta */}
      <div className="flex items-center gap-3">
        {Icon && <Icon className="size-5 text-gray-800" />}
        <span className="text-base font-medium text-slate-800">{label}</span>
      </div>

      {/* Lato Destro: Il Contatore "Pillola" */}
      <div className="flex items-center gap-3 rounded-full bg-white p-1">
        {/* Bottone Meno (Bianco) */}
        <button
          type="button"
          onClick={handleDecrease}
          disabled={value <= min}
          className={`flex size-7 items-center justify-center rounded-full transition-transform active:scale-95 ${
            value <= min
              ? 'cursor-not-allowed bg-gray-400 opacity-50'
              : 'bg-gray-800 text-white hover:bg-gray-600'
          }`}
        >
          <MinusIcon className="size-4 stroke-[3]" />
        </button>

        {/* Valore Numerico */}
        <span className="min-w-[20px] text-center text-lg font-bold text-black">{value}</span>

        {/* Bottone Più (Giallo) */}
        <button
          type="button"
          disabled={value >= max}
          onClick={handleIncrease}
          className={`flex size-7 items-center justify-center rounded-full transition-transform active:scale-95 ${
            value >= max
              ? 'cursor-not-allowed bg-gray-400 opacity-50'
              : 'bg-gray-800 text-white hover:bg-gray-600'
          }`}
        >
          <PlusIcon className="size-4 stroke-[3]" />
        </button>
      </div>
    </div>
  );
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const VAT_RATE = 0.1;

const formatEur = amount =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);

const formatDate = dateStr => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const IconCalendar = () => (
  <svg
    className="size-3.5 shrink-0 text-gray-400"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <rect x="2" y="3" width="12" height="11" rx="2" />
    <path d="M5 1v2M11 1v2M2 7h12" />
  </svg>
);

const IconClock = () => (
  <svg
    className="size-3.5 shrink-0 text-gray-400"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <circle cx="8" cy="8" r="6" />
    <path d="M8 5v3l2 2" />
  </svg>
);

/*const IconRoute = () => (
  <svg
    className="size-4 shrink-0 text-gray-400"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <circle cx="6" cy="19" r="3" />
    <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
    <circle cx="18" cy="5" r="3" />
  </svg>
);*/

/*const IconCar = () => (
  <svg
    className="size-4 shrink-0 text-gray-400"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M5 17H3v-5l2-5h14l2 5v5h-2M5 17h14M5 17a2 2 0 1 0 4 0M15 17a2 2 0 1 0 4 0" />
  </svg>
);*/

const TripBlock = ({ label, date, time }) => (
  <div className="rounded-lg bg-gray-50 p-3">
    <p className="mb-2 text-[10px] uppercase tracking-widest text-gray-400">{label}</p>
    <div className="mb-1 flex items-center gap-1.5">
      <IconCalendar />
      <span className="text-[13px] font-medium text-gray-800">{formatDate(date)}</span>
    </div>
    <div className="flex items-center gap-1.5">
      <IconClock />
      <span className="text-[13px] font-medium text-gray-800">{time}</span>
    </div>
  </div>
);

const PriceRow = ({ label, sub, amount }) => (
  <div className="flex items-start justify-between border-b border-gray-100 py-2.5 last:border-0">
    <div>
      <p className="text-[13px] text-gray-800">{label}</p>
      {sub && <p className="mt-0.5 text-[11px] text-gray-400">{sub}</p>}
    </div>
    <span className="ml-4 whitespace-nowrap text-[13px] font-medium text-gray-800">
      {formatEur(amount)}
    </span>
  </div>
);

const COUNTER_OPTIONS = [
  {
    id: 'childSeat',
    label: 'Seggiolino per bambini',
    description: 'Adatto a bambini da 0 a 18 kg (0–4 anni circa)',
    unitPrice: 15,
  },
  {
    id: 'boosterSeat',
    label: 'Seggiolino',
    description: 'Adatto a bambini da 15 a 36 kg (4–10 anni circa)',
    unitPrice: 20,
  },
  {
    id: 'flowers',
    label: 'Mazzo di fiori',
    description: 'Composizione di stagione preparata da un fiorista locale',
    unitPrice: 50,
  },
];

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    className={`relative inline-flex h-[22px] w-10 shrink-0 cursor-pointer items-center rounded-full border transition-colors duration-200 focus:outline-none ${
      checked ? 'border-gray-900 bg-gray-900' : 'border-gray-200 bg-gray-100'
    }`}
  >
    <span
      className={`inline-block size-4 rounded-full bg-white shadow-sm transition-transform duration-200${
        checked ? 'translate-x-[19px]' : 'translate-x-[3px]'
      }`}
    />
  </button>
);

const Counter = ({ value, onDecrement, onIncrement }) => (
  <div className="flex shrink-0 items-center gap-2.5">
    <button
      type="button"
      onClick={onDecrement}
      disabled={value === 0}
      className="flex size-7 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-500 transition-all hover:border-gray-400 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-30"
    >
      <svg viewBox="0 0 14 2" className="size-3 fill-current">
        <rect width="14" height="2" rx="1" />
      </svg>
    </button>

    <span className="min-w-[18px] text-center text-[14px] font-medium text-gray-800">{value}</span>

    <button
      type="button"
      onClick={onIncrement}
      className="flex size-7 items-center justify-center rounded-full border border-gray-900 bg-gray-900 text-white transition-all hover:bg-gray-700"
    >
      <svg viewBox="0 0 14 14" className="size-3 fill-current">
        <path d="M6 0h2v14H6zM0 6h14v2H0z" />
      </svg>
    </button>
  </div>
);

const Book = () => {
  const [passengers, setPassengers] = useState({
    value: 3,
    min: 1,
    max: 3,
  });

  const [luggage, setLuggage] = useState({
    value: 2,
    min: 0,
    max: 2,
  });

  const [locations, setLocations] = React.useState([]);

  React.useEffect(() => {
    const fetchEntries = async () => {
      try {
        const snapshotRoutes = await getDocs(collection(db, 'routes'));
        const locationsSet = new Set();
        snapshotRoutes.forEach(doc => {
          const data = doc.data();
          if (data.origin) locationsSet.add(data.origin);
          if (data.destination) locationsSet.add(data.destination);
        });
        console.debug(locationsSet);
        setLocations(Array.from(locationsSet));
      } catch (err) {
        console.error(err);
      }
    };

    fetchEntries();
  }, []);

  const setLuggageValue = value => setLuggage(luggage => ({ ...luggage, value: value }));
  const setPassengersValue = value =>
    setPassengers(passengers => ({ ...passengers, value: value }));

  const [plans, setPlans] = useState([
    {
      id: 'e-class',
      name: 'Mercedes-Benz E-Class',
      ram: '2 Bagagli a mano',
      cpus: '3 Passeggeri, 2 Valigie',
      disk: 'Business Class',
      price: 45,
    },
    {
      id: 's-class',
      name: 'Mercedes-Benz S-Class',
      ram: '2 Bagagli a mano',
      cpus: '3 Passeggeri, 2 Valigie',
      disk: 'Premium Class',
      price: 80,
    },
    {
      id: 'v-class',
      name: 'Mercedes-Benz V-Class',
      ram: '6 Bagagli a mano',
      cpus: '6/7 Passeggeri, 6 Valigie',
      disk: 'Business Van/SUV',
      price: 50,
    },
    {
      id: 'sprinter',
      name: 'Mercedes Sprinter',
      ram: '19 Bagagli a mano',
      cpus: '19 Passeggeri, 19 Valigie',
      disk: 'Business Van/SUV',
      price: 100,
    },
  ]);

  const [state, setState] = useState({
    depDate: '',
    depTime: '08:00',
    origin: '',
    destination: '',
    roundTrip: false,
    retDate: '',
    retTime: '18:00',
    terms: false,
    extraStopLocation: '',
  });

  const [extraStop, setExtraStop] = useState(false);
  const [extraStopLocation, setExtraStopLocation] = React.useState('');
  const handleExtraStop = () => {
    const next = !extraStop;
    setExtraStop(() => next);
    if (!next) {
      setExtraStopLocation(() => '');
    }
  };
  const navigate = useNavigate();

  const { bookingData } = useBooking();

  const [steps, setSteps] = useState([]);

  //const [price, setPrice] = useState(null);

  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState({
    text: '',
    value: '',
  });

  const auto = useMemo(() => {
    return [
      {
        id: 'e-class',
        pricePerKm: 2.2,
        init: 45,
        passengers: 3,
        luggage: 2,
      },
      {
        id: 'v-class',
        pricePerKm: 2.6,
        init: 50,
        passengers: 6,
        luggage: 6,
      },
      {
        id: 's-class',
        pricePerKm: 4.5,
        init: 80,
        passengers: 3,
        luggage: 2,
      },
      {
        id: 'sprinter',
        pricePerKm: 6.5,
        init: 100,
        passengers: 19,
        luggage: 19,
      },
      // More users...
    ];
  }, []);

  const [vehicle, setVehicle] = useState('s-class');

  React.useEffect(() => {
    const calculatePrice = async (bookingData, vehicle) => {
      //setDistance(null);
      //setPrice(null);
      //setDuration({ text: '', value: '' });

      //e.preventDefault();

      setState(() => ({
        ...bookingData,
      }));
      setSteps(() => {
        return [
          {
            name: bookingData.origin,
            description: 'Luogo di partenza',
            href: 'A',
            status: 'complete',
          },
          {
            name: bookingData.destination,
            description: 'Luogo di arrivo',
            href: 'B',
            status: 'complete',
          },
        ];
      });

      try {
        const res = await fetch('/.netlify/functions/get-distance-km', {
          method: 'POST',
          headers: { 'x-api-key': 'abcDEF123456789!@#' },
          body: JSON.stringify({
            origin: bookingData.origin,
            destination: bookingData.destination,
            selectedVehicle: vehicle,
          }),
        });

        const data = await res.json();

        setDistance(() => Number(data.distance.value / 1000).toFixed(2));
        //const calculatedPrice = calculatePrice(data.distance.value / 1000).toFixed(2);
        //setPrice(() => data.price);
        setDuration(() => data.duration);

        setPlans(plans => {
          return plans.map(plan => {
            const a = auto.find(item => item.id === plan.id);
            if (a !== undefined) {
              return {
                ...plan,
                price: (a.pricePerKm * data.distance.value) / 1000 + a.init,
              };
            } else {
              return {
                ...plan,
              };
            }
          });
        });
      } catch (err) {
        console.error(err);
      }
    };
    calculatePrice(bookingData, vehicle);
  }, [auto, bookingData, vehicle]);

  const handleSubmit = async e => {
    e.preventDefault();

    const selectedVehicle = plans.find(item => item.id === vehicle);

    const body = {
      ...state,
      vehicle,
      extraStop,
      countChair: counters.childSeat,
      countFlowers: counters.flowers,
      countHighChair: counters.boosterSeat,
      passengers,
      luggage,
      pickup: steps.at(0)?.name,
      destination: steps.at(1)?.name,
      duration: duration.text,
      distance: distance,
      price: Number(selectedVehicle?.price ?? 0).toFixed(2),
      status: 'PENDING',
    };

    const emailPayload = {
      customerEmail: state.email,
      customerName: `${state.firstName} ${state.lastName}`,
      vehicle: selectedVehicle?.name,
      luggage: luggage.value,
      passengers: passengers.value,
      pickup: steps.at(0)?.name,
      destination: steps.at(1)?.name,
      date: state.depDate,
      time: state.depDate,
      duration: duration.text,
      distance: distance,
      price: Number(selectedVehicle?.price ?? 0).toFixed(2),
    };

    const saveToFirebase = addDoc(collection(db, 'quotes'), { ...body });

    const sendEmail = fetch('/.netlify/functions/send-confirmation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emailPayload),
    });

    const submitNetlifyForm = fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ 'form-name': 'quote-request', ...body }).toString(),
    });

    try {
      const results = await Promise.allSettled([saveToFirebase, sendEmail, submitNetlifyForm]);

      // Log degli esiti per debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Chiamata ${index} fallita:`, result.reason);
        }
      });

      // 4. Navigazione (indipendente dagli errori delle singole chiamate)
      setTimeout(() => {
        navigate('/thank-you');
      }, 1500);
    } catch (globalErr) {
      // Questo catch scatta solo se c'è un errore critico nel codice sincrono
      console.error('Errore critico nel processo di invio:', globalErr);
    }

    /*

    try {
      const result = await addDoc(collection(db, 'quotes'), {
        ...body,
      });
      console.info(result.id);
    } catch (err) {
      console.error(err);
    }*/

    /*await fetch('/.netlify/functions/send-confirmation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerEmail: state.email,
        customerName: `${state.firstName} ${state.lastName}`,
        vehicle: plans.find(item => item.id === vehicle)?.name,
        luggage: luggage.value,
        passengers: passengers.value,
        pickup: steps.at(0)?.name,
        destination: steps.at(1)?.name,
        date: state.date,
        time: state.time,
        duration: duration.text,
        distance: distance,
        price: Number(plans.find(plan => plan.id === vehicle)?.price ?? 0).toFixed(2),
        
      }),
    });*/
    /*fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({ 'form-name': 'contatto', ...body }),
    })
      .then(result => {
        console.info(result);

        setTimeout(() => {
          navigate('/thank-you');
        }, 1500);
      })
      .catch(err => {
        console.error(err);
      });*/
  };

  /*const [countHighChair, setCountHighChair] = useState(0);
  const handleDecreaseHighChair = () => {
    if (countHighChair >= 0) setCountHighChair(count => count - 1);
  };

  const handleIncreaseHighChair = () => {
    setCountHighChair(count => count + 1);
  };*/

  /*const [countChair, setCountChair] = useState(0);
  const handleDecreaseChair = () => {
    if (countChair >= 0) setCountChair(count => count - 1);
  };

  const handleIncreaseChair = () => {
    setCountChair(count => count + 1);
  };*/

  /*const [countFlowers, setCountFlowers] = useState(0);
  const handleDecreaseFlowers = () => {
    if (countFlowers > 0) setCountFlowers(count => count - 1);
  };

  const handleIncreaseFlowers = () => {
    setCountFlowers(count => count + 1);
  };*/

  const handleVehicleChange = e => {
    const v = e.target.value;
    const a = auto.find(a => a.id === v);
    setVehicle(e.target.value);
    if (a !== undefined) {
      setLuggage(() => ({
        max: a.luggage,
        min: 0,
        value: a.luggage,
      }));
      setPassengers(() => ({
        max: a.passengers,
        min: 1,
        value: a.passengers,
      }));
    }
  };

  const [counters, setCounters] = useState({
    childSeat: 0,
    boosterSeat: 0,
    flowers: 0,
  });

  const handleCounter = (id, delta) => {
    const next = Math.max(0, (counters[id] || 0) + delta);
    const updated = { ...counters, [id]: next };
    setCounters(updated);
    //notify({ counters: updated });
  };

  const subtotal = [
    {
      service: 'countHighChair',
      name: 'Seggiolino per bambini',
      value: counters.boosterSeat,
      price: 15,
    },
    {
      service: 'countChair',
      name: 'Seggiolino',
      value: counters.childSeat,
      price: 20,
    },
    {
      service: 'countFlowers',
      value: counters.flowers,
      name: 'Mazzo di fiori',
      price: 50,
    },
    /*{
      service: 'extraStop',
      value: extraStop,
      price: 20,
      name: 'Extra Stop',
    },*/
  ].reduce(
    (sum, e) => {
      const val = typeof e.value === 'number' ? e.value : 0;
      return sum + e.price * val;
    },
    plans.find(plan => plan.id === vehicle)?.price ?? 0
  );
  const vat = subtotal * VAT_RATE;
  const total = subtotal + vat;

  return (
    <div className="mx-auto my-12 max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
      <h1 className="sr-only">Book your trip</h1>
      {/* Main 3 column grid */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 lg:gap-8">
        {/* Left column */}
        <div className="grid grid-cols-1 gap-4 lg:col-span-2">
          <section aria-labelledby="main-tab">
            <h2 id="main-tab" className="sr-only">
              plan your journey
            </h2>
            <div className="overflow-hidden rounded-lg bg-white">
              <div className="p-6">
                <div className="mb-12 px-4 sm:px-0">
                  <h3 className="text-base/7 font-semibold text-gray-900">Transporto</h3>
                  <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">Seleziona la tua auto</p>
                </div>
                <fieldset aria-label="Vehicle">
                  <div className="space-y-4">
                    {plans.map(plan => (
                      <label
                        key={plan.id}
                        aria-label={plan.name}
                        aria-description={`${plan.ram}, ${plan.cpus}, ${plan.disk}, ${plan.price} per month`}
                        className="group relative block rounded-lg border border-gray-300 bg-white px-6 py-4 has-[:checked]:outline has-[:focus-visible]:outline has-[:checked]:outline-2 has-[:focus-visible]:outline-[3px] has-[:checked]:-outline-offset-2 has-[:focus-visible]:-outline-offset-1 has-[:checked]:outline-primary sm:flex sm:justify-between"
                      >
                        <input
                          value={plan.id}
                          checked={plan.id === vehicle}
                          name="plan"
                          type="radio"
                          onChange={handleVehicleChange}
                          className="absolute inset-0 hidden appearance-none focus:outline focus:outline-0"
                        />
                        <span className="flex items-center">
                          <span className="flex flex-col text-sm">
                            <span className="font-medium text-gray-900">{plan.name}</span>
                            <span className="text-gray-500">
                              <span className="block sm:inline">
                                {plan.cpus}/{plan.ram}
                              </span>{' '}
                              <span aria-hidden="true" className="hidden sm:mx-1 sm:inline">
                                &middot;
                              </span>{' '}
                              <span className="block sm:inline">{plan.disk}</span>
                            </span>
                          </span>
                        </span>
                        <span className="mt-2 flex text-sm sm:ml-4 sm:mt-0 sm:flex-col sm:text-right">
                          <span className="font-medium text-gray-900">
                            {`EUR ${Number(plan.price).toFixed(2)}`}
                          </span>
                          {/*<span className="ml-1 text-gray-500 sm:ml-0">/mo</span>*/}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div className="mx-auto max-w-4xl pt-8">
                  {/* Griglia per i due elementi */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8">
                    {/* Selettore Passeggeri */}
                    <CounterItem
                      label="Passeggeri"
                      icon={UsersIcon}
                      value={passengers.value}
                      onChange={setPassengersValue}
                      min={passengers.min}
                      max={passengers.max}
                    />

                    {/* Selettore Bagaglio */}
                    <CounterItem
                      label="Bagagli"
                      icon={BriefcaseIcon}
                      value={luggage.value}
                      onChange={setLuggageValue}
                      min={luggage.min}
                      max={luggage.max}
                    />
                  </div>
                </div>
                <div className="my-12 w-full">
                  {/* Header */}
                  <div className="mb-4">
                    <p className="text-[15px] font-medium text-gray-900">Opzioni aggiuntive</p>
                    <p className="mt-0.5 text-[12px] text-gray-400">
                      Seleziona i servizi più adatti
                    </p>
                  </div>

                  {/* Opzioni con counter */}
                  <div className="flex flex-col gap-2.5">
                    {COUNTER_OPTIONS.map(opt => (
                      <div
                        key={opt.id}
                        className="rounded-xl border border-gray-200 bg-white px-4 py-3.5 transition-colors hover:border-gray-300"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-medium text-gray-800">{opt.label}</p>
                            <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">
                              {opt.description}
                            </p>
                          </div>
                          <Counter
                            value={counters[opt.id]}
                            onDecrement={() => handleCounter(opt.id, -1)}
                            onIncrement={() => handleCounter(opt.id, +1)}
                          />
                        </div>
                      </div>
                    ))}

                    {/* Extra Stop con toggle + input condizionale */}
                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3.5 transition-colors hover:border-gray-300">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] font-medium text-gray-800">Extra Stop</p>
                          <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">
                            Sosta aggiuntiva lungo il percorso
                          </p>
                        </div>
                        <Toggle checked={extraStop} onChange={handleExtraStop} />
                      </div>

                      {/* Input luogo sosta — animato */}
                      <div
                        className={`overflow-visible transition-all duration-300 ease-in-out ${
                          extraStop ? 'mt-3 max-h-20 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <AutoCompleteInput
                          label=""
                          name="extraStopLocation"
                          placeholder="Inserisci luogo"
                          value={extraStopLocation}
                          onChange={setExtraStopLocation}
                          options={locations}
                        />
                        {/*<input
                          type="text"
                          value={extraStopLocation}
                          onChange={handleStopLocation}
                          placeholder="Inserisci il luogo della sosta..."
                          autoComplete="off"
                          className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-[13px] text-gray-800 outline-none transition-all placeholder:text-gray-300 focus:border-gray-900 focus:bg-white"
                        />*/}
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="my-4 h-px bg-gray-100" />

                  {/* Note per l'autista */}
                  <div>
                    <label
                      htmlFor="notes"
                      className="mb-2 block text-[13px] font-medium text-gray-800"
                    >
                      Note per l&apos;autista
                    </label>
                    <textarea
                      id="notes"
                      value={state.notes}
                      onChange={e => setState(s => ({ ...s, notes: e.target.value }))}
                      placeholder="Eventuali indicazioni, richieste particolari o informazioni utili per l'autista..."
                      rows={3}
                      className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-[13px] leading-relaxed text-gray-800 outline-none transition-all placeholder:text-gray-300 focus:border-gray-900 focus:bg-white"
                    />
                  </div>
                </div>
                {/*<div className="my-12 px-4 sm:px-0">
                  <h3 className="text-base/7 font-semibold text-gray-900">Opzioni aggiuntive</h3>
                  <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
                    Seleziona i servizi più adatti
                  </p>
                </div>*/}
                {/*<div className="flex items-center justify-between">
                  
                  <span className="flex grow flex-col">
                    <p id="counter-label" className="text-sm/6 font-medium text-gray-900">
                      Seggiolino per bambini
                    </p>
                    <p id="counter-description" className="text-sm text-gray-500">
                      Adatto a bambini di peso compreso tra 0 e 18 kg (da 0 a 4 anni circa).
                    </p>
                  </span>

                  
                  <div className="flex shrink-0 items-center gap-3 rounded-full bg-white p-1">
                    
                    <button
                      type="button"
                      onClick={handleDecreaseHighChair}
                      //disabled={value <= min}
                      className={`flex size-7 items-center justify-center rounded-full transition-transform active:scale-95 ${
                        countHighChair <= 0
                          ? 'cursor-not-allowed bg-gray-400 opacity-50'
                          : 'bg-gray-800 text-white hover:bg-gray-600'
                      }`}
                    >
                      <MinusIcon className="size-4 stroke-[3]" />
                    </button>

                    
                    <span className="min-w-[20px] select-none text-center text-lg font-bold text-black">
                      {countHighChair}
                    </span>

                   
                    <button
                      type="button"
                      onClick={handleIncreaseHighChair}
                      className="flex size-7 items-center justify-center rounded-full bg-primary text-white transition-transform hover:bg-primary active:scale-95"
                    >
                      <PlusIcon className="size-4 stroke-[3]" />
                    </button>
                  </div>
                </div>*/}
                {/*<div className="mt-12 flex items-center justify-between">
                  
                  <span className="flex grow flex-col">
                    <p id="counter-label" className="text-sm/6 font-medium text-gray-900">
                      Seggiolino
                    </p>
                    <p id="counter-description" className="text-sm text-gray-500">
                      Adatto a bambini di peso compreso tra 15 e 36 kg (da 4 a 10 anni circa).
                    </p>
                  </span>

                 
                  <div className="flex shrink-0 items-center gap-3 rounded-full bg-white p-1">
                   
                    <button
                      type="button"
                      onClick={handleDecreaseChair}
                      //disabled={value <= min}
                      className={`flex size-7 items-center justify-center rounded-full transition-transform active:scale-95 ${
                        countChair <= 0
                          ? 'cursor-not-allowed bg-gray-400 opacity-50'
                          : 'bg-gray-800 text-white hover:bg-gray-600'
                      }`}
                    >
                      <MinusIcon className="size-4 stroke-[3]" />
                    </button>

                   
                    <span className="min-w-[20px] select-none text-center text-lg font-bold text-black">
                      {countChair}
                    </span>

                    
                    <button
                      type="button"
                      onClick={handleIncreaseChair}
                      className="flex size-7 items-center justify-center rounded-full bg-primary text-white transition-transform hover:bg-primary active:scale-95"
                    >
                      <PlusIcon className="size-4 stroke-[3]" />
                    </button>
                  </div>
                </div>*/}
                {/*<div className="mt-12 flex items-center justify-between">
                  
                  <span className="flex grow flex-col">
                    <p id="counter-label" className="text-sm/6 font-medium text-gray-900">
                      Mazzo di fiori
                    </p>
                    <p id="counter-description" className="text-sm text-gray-500">
                      Un mazzo di fiori di stagione preparato da un fiorista locale.
                    </p>
                  </span>

                  
                  <div className="flex shrink-0 items-center gap-3 rounded-full bg-white p-1">
                    
                    <button
                      type="button"
                      onClick={handleDecreaseFlowers}
                      //disabled={value <= min}
                      className={`flex size-7 items-center justify-center rounded-full transition-transform active:scale-95 ${
                        countFlowers <= 0
                          ? 'cursor-not-allowed bg-gray-400 opacity-50'
                          : 'bg-gray-800 text-white hover:bg-gray-600'
                      }`}
                    >
                      <MinusIcon className="size-4 stroke-[3]" />
                    </button>

                    
                    <span className="min-w-[20px] select-none text-center text-lg font-bold text-black">
                      {countFlowers}
                    </span>

                    
                    <button
                      type="button"
                      onClick={handleIncreaseFlowers}
                      className="flex size-7 items-center justify-center rounded-full bg-primary text-white transition-transform hover:bg-primary active:scale-95"
                    >
                      <PlusIcon className="size-4 stroke-[3]" />
                    </button>
                  </div>
                </div> */}
                {/*<div className="mt-12 flex items-center justify-between">
                  <span className="flex grow flex-col">
                    <label
                      htmlFor="availability"
                      id="availability-label"
                      className="text-sm/6 font-medium text-gray-900"
                    >
                      Extra Stop
                    </label>
                    <span id="availability-description" className="text-sm text-gray-500">
                      Extra Stop
                    </span>
                  </span>
                  <div className="group relative inline-flex w-11 shrink-0 rounded-full bg-gray-200 p-0.5 outline-offset-2 outline-indigo-600 ring-1 ring-inset ring-gray-900/5 transition-colors duration-200 ease-in-out has-[:checked]:bg-indigo-600 has-[:focus-visible]:outline has-[:focus-visible]:outline-2">
                    <Switch
                      checked={extraStop}
                      onChange={handleChangeExtraStop}
                      className={`${
                        extraStop ? 'bg-primary' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          extraStop ? 'translate-x-6' : 'translate-x-1'
                        } inline-block size-4 rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                </div>*/}
                {/*<div className="mt-12">
                  <label htmlFor="about" className="block text-sm/6 font-medium text-gray-900">
                    Note per l&#39;autista
                  </label>
                  <div className="mt-2">
                    <textarea
                      id="about"
                      name="about"
                      placeholder="Note per l&#39;autista"
                      onChange={e => setState(state => ({ ...state, notes: e.target.value }))}
                      rows={3}
                      className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                      value={state.notes}
                    />
                  </div>
                </div>*/}
                <div className="my-12 px-4 sm:px-0">
                  <h3 className="text-base/7 font-semibold text-gray-900">Contatti</h3>
                  <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">Dettagli del passeggero</p>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="sm:col-span-3">
                    <label
                      htmlFor="first-name"
                      className="block text-sm/6 font-medium text-gray-900"
                    >
                      Nome
                    </label>
                    <div className="mt-2">
                      <input
                        required
                        id="first-name"
                        name="firstName"
                        type="text"
                        value={state.firstName}
                        onChange={e => setState(state => ({ ...state, firstName: e.target.value }))}
                        autoComplete="given-name"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                      />
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    <label
                      htmlFor="last-name"
                      className="block text-sm/6 font-medium text-gray-900"
                    >
                      Cognome
                    </label>
                    <div className="mt-2">
                      <input
                        required
                        id="last-name"
                        name="lastName"
                        value={state.lastName}
                        onChange={e => setState(state => ({ ...state, lastName: e.target.value }))}
                        type="text"
                        autoComplete="family-name"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-4">
                    <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                      Email
                    </label>
                    <div className="mt-2">
                      <input
                        required
                        id="email"
                        name="email"
                        type="email"
                        value={state.email}
                        onChange={e => setState(state => ({ ...state, email: e.target.value }))}
                        autoComplete="email"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-4">
                    <label
                      htmlFor="phone-number"
                      className="block text-sm/6 font-medium text-gray-900"
                    >
                      Cellulare
                    </label>
                    <div className="mt-2">
                      <input
                        required
                        id="phone-number"
                        name="telephone"
                        value={state.telephone}
                        onChange={e => setState(state => ({ ...state, telehone: e.target.value }))}
                        type="tel"
                        autoComplete="tel"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
                <div className="px-4 py-5 sm:p-6">
                  {/* Titolo */}
                  <h3 className="text-base font-semibold text-gray-900">Conferma prenotazione</h3>

                  {/* Contenitore Colonna */}
                  <div className="mt-4 flex flex-col space-y-6">
                    {/* 1. Messaggio di conferma */}
                    <p className="text-sm leading-6 text-gray-500">
                      Per confermare la prenotazione, clicca sul pulsante &quot;Conferma&quot;.
                      Riceverai una email di conferma con i dettagli della tua prenotazione e le
                      istruzioni per il pagamento.
                    </p>

                    {/* 2. Accordo Termini e Privacy con Checkbox accanto */}
                    <div className="relative flex items-start">
                      <div className="flex h-6 items-center">
                        {/* Wrapper per il checkbox custom (Input + SVG) */}
                        <div className="grid size-4 grid-cols-1">
                          <input
                            checked={state.terms}
                            onChange={e =>
                              setState(state => ({ ...state, terms: e.target.checked }))
                            }
                            id="comments"
                            name="comments"
                            type="checkbox"
                            aria-describedby="comments-description"
                            className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-primary checked:bg-primary indeterminate:border-primary indeterminate:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                          />
                          <svg
                            fill="none"
                            viewBox="0 0 14 14"
                            className="pointer-events-none col-start-1 row-start-1 size-3.5 place-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                          >
                            <path
                              d="M3 8L6 11L11 3.5"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="opacity-0 group-has-[:checked]:opacity-100"
                            />
                            <path
                              d="M3 7H11"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="opacity-0 group-has-[:indeterminate]:opacity-100"
                            />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3 text-sm leading-6">
                        <label htmlFor="terms" className="cursor-pointer text-gray-500">
                          Sono d&apos;accordo con i{' '}
                          <Link to={'/404'} className="underline">
                            Termini e Condizioni
                          </Link>{' '}
                          e{' '}
                          <Link to="/privacy-policy" className="underline">
                            Policy Privacy
                          </Link>
                          .
                        </label>
                      </div>
                    </div>

                    {/* 3. Pulsante di conferma */}
                    <div className="pt-2">
                      <button
                        type="button"
                        disabled={!state.terms}
                        onClick={handleSubmit}
                        className="inline-flex w-full items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:w-auto"
                      >
                        Conferma
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Right column */}
        <div className="grid grid-cols-1 gap-4">
          <section aria-labelledby="summary-title" className="mx-auto w-full max-w-sm font-sans">
            <h2 id="summary-title" className="sr-only">
              Summary
            </h2>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {/* ── Header ── */}
              <div className="flex items-center justify-between bg-gray-900 px-4 py-3.5">
                <span className="text-[11px] uppercase tracking-widest text-white/50">
                  Riepilogo prenotazione
                </span>
                {state.roundTrip && (
                  <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] text-white/80">
                    Andata e ritorno
                  </span>
                )}
              </div>

              <div className="p-4">
                {false && (
                  <nav aria-label="Progress">
                    <ol className="overflow-hidden">
                      {steps?.map((step, stepIdx) => {
                        return (
                          <li
                            key={step.name}
                            className={classNames(
                              stepIdx !== steps.length - 1 ? 'pb-10' : '',
                              'relative'
                            )}
                          >
                            {step.status === 'complete' && step.name?.length ? (
                              <>
                                {stepIdx !== steps.length - 1 ? (
                                  <div
                                    aria-hidden="true"
                                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-primary"
                                  />
                                ) : null}
                                <a href={'/booking'} className="group relative flex items-start">
                                  <span className="flex h-9 items-center">
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full bg-primary text-white group-hover:bg-primary">
                                      {/*<CheckIcon aria-hidden="true" className="size-5 text-white" />*/}
                                      {step.href}
                                    </span>
                                  </span>
                                  <span className="ml-4 flex min-w-0 flex-col">
                                    <span className="text-sm font-medium text-gray-900">
                                      {step.name}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      {step.description}
                                    </span>
                                  </span>
                                </a>
                              </>
                            ) : step.status === 'current' ? (
                              <>
                                {stepIdx !== steps.length - 1 ? (
                                  <div
                                    aria-hidden="true"
                                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                                  />
                                ) : null}
                                <a
                                  href={step.href}
                                  aria-current="step"
                                  className="group relative flex items-start"
                                >
                                  <span aria-hidden="true" className="flex h-9 items-center">
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-primary bg-white">
                                      <span className="size-2.5 rounded-full bg-primary" />
                                    </span>
                                  </span>
                                  <span className="ml-4 flex min-w-0 flex-col">
                                    <span className="text-sm font-medium text-primary">
                                      {step.name}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      {step.description}
                                    </span>
                                  </span>
                                </a>
                              </>
                            ) : (
                              <>
                                {stepIdx !== steps.length - 1 ? (
                                  <div
                                    aria-hidden="true"
                                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full w-0.5 bg-gray-300"
                                  />
                                ) : null}
                                <a href={step.href} className="group relative flex items-start">
                                  <span aria-hidden="true" className="flex h-9 items-center">
                                    <span className="relative z-10 flex size-8 items-center justify-center rounded-full border-2 border-gray-300 bg-white group-hover:border-gray-400">
                                      <span className="size-2.5 rounded-full bg-transparent group-hover:bg-gray-300" />
                                    </span>
                                  </span>
                                  <span className="ml-4 flex min-w-0 flex-col">
                                    <span className="text-sm font-medium text-gray-500">
                                      {step.name}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      {step.description}
                                    </span>
                                  </span>
                                </a>
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </nav>
                )}
              </div>
              {/* Partenza */}
              <div className="flex items-start gap-3 p-4">
                <div className="flex flex-col items-center">
                  <div className="flex size-6 items-center justify-center rounded-full bg-blue-50 text-[11px] font-medium text-blue-800">
                    A
                  </div>
                  <div className="my-1 w-px flex-1 bg-gray-200" style={{ height: 14 }} />
                </div>
                <div className="min-w-0 pb-2">
                  <p className="text-[13px] font-medium leading-snug text-gray-800">
                    {state.origin || '—'}
                  </p>
                  <p className="mt-0.5 text-[11px] text-gray-400">Luogo di partenza</p>
                </div>
              </div>

              {/* Destinazione */}
              <div className="flex items-start gap-3 p-4">
                <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-900 text-[11px] font-medium text-white">
                  B
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium leading-snug text-gray-800">
                    {state.destination || '—'}
                  </p>
                  <p className="mt-0.5 text-[11px] text-gray-400">Luogo di arrivo</p>
                </div>
              </div>
              {/* ── Date e orari ── */}
              <div className="border-t border-gray-100 p-4">
                <div className={`grid gap-2 ${state.roundTrip ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <TripBlock label="Andata" date={state.depDate} time={state.depTime} />
                  {state.roundTrip && (
                    <TripBlock label="Ritorno" date={state.retDate} time={state.retTime} />
                  )}
                </div>
              </div>

              {/* ── Distanza e tempo ── */}
              <div className="border-t border-gray-100">
                <div className="grid grid-cols-2">
                  <div className="border-r border-gray-100 py-3 text-center">
                    <p className="text-[17px] font-medium text-gray-800">
                      {distance ? `${distance} km` : '— km'}
                    </p>
                    <p className="mt-0.5 text-[11px] text-gray-400">Distanza totale</p>
                  </div>
                  <div className="py-3 text-center">
                    <p className="text-[17px] font-medium text-gray-800">{duration?.text || '—'}</p>
                    <p className="mt-0.5 text-[11px] text-gray-400">Tempo stimato</p>
                  </div>
                </div>
              </div>
              {/* ── Voci di prezzo ── */}
              <div className="border-t border-gray-100 px-4 py-3">
                {/* Veicolo */}
                <PriceRow
                  label={plans.find(plan => plan.id === vehicle).name || 'Veicolo'}
                  sub="Veicolo selezionato"
                  amount={Number(plans.find(plan => plan.id === vehicle)?.price ?? 0).toFixed(2)}
                />

                {/* Extra */}
                {[
                  {
                    service: 'countHighChair',
                    name: 'Seggiolino per bambini',
                    value: counters.boosterSeat,
                    price: 15,
                  },
                  {
                    service: 'countChair',
                    name: 'Seggiolino',
                    value: counters.childSeat,
                    price: 20,
                  },
                  {
                    service: 'countFlowers',
                    value: counters.flowers,
                    name: 'Mazzo di fiori',
                    price: 50,
                  },
                  /*{
                    service: 'extraStop',
                    value: extraStop,
                    price: 20,
                    name: 'Extra Stop',
                  },*/
                ].map((extra, i) => (
                  <PriceRow
                    key={i}
                    label={extra.name}
                    sub={extra.value > 1 ? `× ${extra.value}` : undefined}
                    amount={extra.price * (extra.value || 1)}
                  />
                ))}

                {/* IVA */}
                <div className="mt-1 flex items-center justify-between border-t border-dashed border-gray-200 pt-2.5">
                  <span className="text-[12px] text-gray-400">
                    IVA {(VAT_RATE * 100).toFixed(0)}%
                  </span>
                  <span className="text-[12px] text-gray-400">{formatEur(vat)}</span>
                </div>
              </div>

              {/* ── Totale ── */}
              <div className="flex items-center justify-between bg-gray-900 p-4">
                <span className="text-[13px] font-medium text-white/60">Totale</span>
                <span className="text-[20px] font-medium text-white">{formatEur(total)}</span>
              </div>
              {/*<div className="mt-4 flex w-full flex-none gap-x-4 px-6">
                <dt className="flex-none">
                  <span className="sr-only">Due date</span>
                  <CalendarDaysIcon aria-hidden="true" className="size-6 text-gray-400" />
                </dt>
                <dd className="text-sm/6 text-gray-500">
                  <time dateTime={state.depDate}>{state.depDate}</time>
                </dd>
              </div>*/}
              {/*<div className="mt-4 flex w-full flex-none gap-x-4 px-6">
                <dt className="flex-none">
                  <span className="sr-only">Status</span>
                  <ClockIcon aria-hidden="true" className="size-6 text-gray-400" />
                </dt>
                <dd className="text-sm/6 text-gray-500">{state.depTime}</dd>
              </div>*/}
              {/*state.roundTrip && (
                <>
                  <div className="mt-4 flex w-full flex-none gap-x-4 px-6">
                    <dt className="flex-none">
                      <span className="sr-only">Due date</span>
                      <CalendarDaysIcon aria-hidden="true" className="size-6 text-gray-400" />
                    </dt>
                    <dd className="text-sm/6 text-gray-500">
                      <time dateTime={state.retDate}>{state.retDate}</time>
                    </dd>
                  </div>
                  <div className="mt-4 flex w-full flex-none gap-x-4 px-6">
                    <dt className="flex-none">
                      <span className="sr-only">Status</span>
                      <ClockIcon aria-hidden="true" className="size-6 text-gray-400" />
                    </dt>
                    <dd className="text-sm/6 text-gray-500">{state.retTime}</dd>
                  </div>
                </>
              )*/}
              {/*<div className="flex items-center gap-8 rounded-lg bg-white p-4">
                <div className="flex flex-col">
                  <span className="text-sm text-gray-500">Distanza totale</span>

                  <span className="text-sm font-medium text-gray-900">{distance} km</span>
                </div>

                <div className="h-8 w-px bg-gray-200"></div>

                <div className="flex flex-col">
                  <span className="text-sm text-gray-500">Tempo totale</span>
                  <span className="text-sm font-medium text-gray-900">{duration?.text}</span>
                </div>
              </div>*/}
              {/*<dl className="space-y-6 border-t border-gray-200 pt-6 text-sm font-medium text-gray-500">
                <div className="flex justify-between">
                  <dt>{plans.find(plan => plan.id === vehicle).name}</dt>
                  <dd className="text-gray-900">
                    {`EUR ${Number(plans.find(plan => plan.id === vehicle)?.price ?? 0).toFixed(2)}`}
                  </dd>
                </div>
                {[
                  {
                    service: 'countHighChair',
                    name: 'Seggiolino per bambini',
                    value: countHighChair,
                    price: 15,
                  },
                  {
                    service: 'countChair',
                    name: 'Seggiolino',
                    value: countChair,
                    price: 20,
                  },
                  {
                    service: 'countFlowers',
                    value: countFlowers,
                    name: 'Mazzo di fiori',
                    price: 50,
                  },
                  {
                    service: 'extraStop',
                    value: extraStop,
                    price: 20,
                    name: 'Extra Stop',
                  },
                ]
                  .filter(service => service.price * service.value > 0)
                  .map(service => {
                    if (service.service !== 'extraStop')
                      return (
                        <div key={service.service} className="flex justify-between">
                          <dt>{`${service.name} (x${service.value})`}</dt>
                          <dd className="text-gray-900">
                            {`EUR ${Number(service.price * service.value).toFixed(2)}`}
                          </dd>
                        </div>
                      );
                    else {
                      return (
                        <div key={service.service} className="flex justify-between">
                          <dt>{service.name}</dt>
                          <dd className="text-gray-900">{`EUR ${Number(service.price * service.value).toFixed(2)}`}</dd>
                        </div>
                      );
                    }
                  })}

                <div className="flex justify-between">
                    <dt>Shipping</dt>
                    <dd className="text-gray-900">$8.00</dd>
                  </div>

                  <div className="flex justify-between">
                    <dt>Taxes</dt>
                    <dd className="text-gray-900">$6.40</dd>
                  </div>

                <div className="flex items-center justify-between border-t border-gray-200 pt-6 text-gray-900">
                  <dt className="text-base">Totale</dt>
                  <dd className="text-base">
                    {`EUR ${Number(
                      [
                        {
                          service: 'countHighChair',
                          name: 'Seggiolino per bambini',
                          value: countHighChair,
                          price: 15,
                        },
                        {
                          service: 'countChair',
                          name: 'Seggiolino',
                          value: countChair,
                          price: 20,
                        },
                        {
                          service: 'countFlowers',
                          value: countFlowers,
                          name: 'Mazzo di fiori',
                          price: 50,
                        },
                        {
                          service: 'extraStop',
                          value: extraStop,
                          price: 20,
                          name: 'Extra Stop',
                        },
                      ].reduce(
                        (sum, item) => {
                          //const value = typeof item.value === 'number' ? item.value : 0;
                          return sum + item.price * item.value;
                        },
                        plans.find(plan => plan.id === vehicle)?.price ?? 0
                      )
                    ).toFixed(2)}`}
                  </dd>
                </div>
              </dl>*/}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Book;
